/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { DownloadIcon } from "@chakra-ui/icons"
import {
  Box, Button, Flex, Heading, Spinner, Text, Tooltip, type ChakraProps
} from "@chakra-ui/react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { SnapPopoverNotes } from "../../components/commons/popover"
import { Colors } from "../../theme/colors"
import { useFilterProductLazyQuery, useGetReportLazyQuery, type ProductFilterInput } from "../../type-and-hooks"
import { buildDocumentUrl } from "../../common/documentUrl"
import { useReportError, verifyPdfAccessible } from "../../common/commonHooks/useReportError"
import { useProductFilterData } from "../fmp/productFilter/producFilter.data.hook"
import { PDFIcon } from "../../components/productInfo/product-info.main"
import { useAtom } from "jotai"
import { extractValidObjects, hasMandatoryFields } from "../../components/commons/utilsTs"
import { applyBestMatchFilter } from "../fmp/productFilter/productFilter.support.bestMatch"
import { specialTypeSwap } from "../fmp/productFilter/productFilter.support"
import { FMPPagination } from "../fmp/productFilter/productFilter.support.pagination"
import { productFilterFormDataAtom } from "../fmp/productFilter/productFilter.support.store"
import { DEFAULT_RESULTS_VIEW_MODE, ResultsViewModeRadio, type ResultsViewMode } from "../fmp/productFilter/productFilter.support.viewMode"

const tableStyleHeader: ChakraProps = {
  bg: Colors.Primary.accent,
  color: "white",
  fontWeight: "700",
  textAlign: "center",
  alignContent: "center",
  py: 1,
  fontSize: "12px",
  fontFamily: "PT Sans"
}

const nameColumnStyles: ChakraProps = { minW: "150px" }

const columnStyles = {
  minH: "40px",
  bg: "white",
  p: "1",
  alignItems: "center",
  borderBottom: `1px solid ${Colors.Secondary.c2_300}`
}

const buttonProps = { fontSize: "12px", variant: "linkButton", fontWeight: "normal" }

export const PublicFilterTable = () => {
  const navigate = useNavigate()
  const [isScrolled, setIsScrolled] = useState(false)
  const tableRef = useRef<HTMLDivElement>(null)
  const [productFilterFormData] = useAtom(productFilterFormDataAtom)
  const [filterProduct, { data: tableData, loading }] = useFilterProductLazyQuery()
  const [getReport, { loading: reportLoading }] = useGetReportLazyQuery()
  const { reportError } = useReportError()

  const [activePage, setActivePage] = useState<number>(0)
  const [numberOfRowsPerPage, setNumberOfRowsPerPage] = useState<number>(10)
  const [downloadLoading, setDownloadLoading] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<ResultsViewMode>(DEFAULT_RESULTS_VIEW_MODE)

  const formChangeHandler = useCallback((): void => {
    const nullExcludedFormValues = extractValidObjects(productFilterFormData, true)
    const keys = Object.keys(nullExcludedFormValues) as Array<keyof ProductFilterInput>
    const specialTypes: ProductFilterInput = keys.reduce((acc, key) => ({
      ...acc, [key]: specialTypeSwap<unknown>(nullExcludedFormValues[key] ?? "")
    }), {})
    if (specialTypes && hasMandatoryFields(specialTypes)) {
      filterProduct({ variables: { criteria: { ...specialTypes } } }).catch(() => {})
    }
  }, [productFilterFormData, filterProduct])

  const viewModeResultsData = viewMode === "best"
    ? applyBestMatchFilter(tableData?.filterProduct)
    : tableData?.filterProduct
  const { data } = useProductFilterData(viewModeResultsData)

  useEffect(() => {
    formChangeHandler()
    setActivePage(0)
    setViewMode(DEFAULT_RESULTS_VIEW_MODE)
  }, [formChangeHandler])

  useEffect(() => {
    const handleScroll = () => {
      if (tableRef.current) setIsScrolled(tableRef.current.scrollTop > 0)
    }
    const el = tableRef.current
    if (el) el.addEventListener("scroll", handleScroll)
    return () => { if (el) el.removeEventListener("scroll", handleScroll) }
  }, [])

  const handleProductClick = (productGenericCode: string) => {
    navigate(`/products/fire-collar/${encodeURIComponent(productGenericCode)}`)
  }

  const handleReportClick = async (report_name: string): Promise<void> => {
    try {
      const { data: reportData } = await getReport({ variables: { reportNo: report_name } })
      const url = reportData?.getReport?.reportURL ?? null
      if (!url) throw new Error("Report URL not found")
      const accessible = await verifyPdfAccessible(url)
      if (!accessible) {
        reportError({ context: "Inaccessible Report", detail: `Report "${report_name}" returned access denied on public product filter page` })
        return
      }
      window.open(buildDocumentUrl(reportData?.getReport?.fileName, url) ?? url, "_blank", "noopener,noreferrer")
    } catch {
      reportError({ context: "Missing Report", detail: `Report "${report_name}" failed to load on public product filter page` })
    }
  }

  const handleDirectDownload = async (report_name: string): Promise<void> => {
    setDownloadLoading(report_name)
    try {
      const { data: reportData } = await getReport({ variables: { reportNo: report_name } })
      const url = reportData?.getReport?.reportURL ?? null
      const name = reportData?.getReport?.reportName ?? report_name
      if (!url) throw new Error("Report URL not found")
      const downloadUrl = reportData?.getReport?.downloadURL ?? url
      const accessible = await verifyPdfAccessible(url)
      if (!accessible) {
        reportError({ context: "Inaccessible Report", detail: `Report "${report_name}" returned access denied on public product filter page` })
        return
      }
      const link = document.createElement("a")
      link.href = downloadUrl
      link.download = `${name}.pdf`
      link.target = "_blank"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch {
      reportError({ context: "Missing Report", detail: `Report "${report_name}" failed to download on public product filter page` })
    } finally {
      setDownloadLoading(null)
    }
  }

  // Public headers: skip first "Add to FMP" column
  const publicHeaders = data?.headers?.slice(1) ?? []

  return (
    <>
      <Box mt="1">
        <Box>
          <Heading pb="10px" variant={"RHeading3CAPS"} display={"inline-block"} borderBottom={`2px solid ${Colors.Primary.accent}`}>
            Filter Results
          </Heading>
        </Box>

        {Boolean(tableData?.filterProduct) && (
          <Flex justifyContent="flex-start" mb="2">
            <ResultsViewModeRadio value={viewMode} onChange={setViewMode} />
          </Flex>
        )}

        <Flex gap="16px" alignItems="flex-end" justifyContent="flex-end" pos={"relative"} w="100%">
          <FMPPagination
            handleNumberOfRowsPerPage={rows => { setNumberOfRowsPerPage(rows); setActivePage(0) }}
            data={data}
            numberOfRowsPerPage={numberOfRowsPerPage}
            activePage={activePage}
            setActivePage={setActivePage}
          />
        </Flex>

        {loading && (
          <Flex w="100%" m="4" align="center" justify={"center"}>
            <Spinner />
          </Flex>
        )}

        <Box>
          <Box
            ref={tableRef}
            boxShadow={"0 8px 8px #ddd"}
            overflow={"auto"}
            mt="0"
            height="520px"
            opacity={data ? 1 : 0}
            transition={"400ms"}
            minHeight="300px"
            minW="900px"
          >
            {data?.headers && !loading && (
              <>
                {data?.columns?.length === 0 ? (
                  <Flex height="300px" width="100%" alignItems="center" justifyContent="center" bg="white">
                    <Text color="gray.500" fontSize="14px">No records found</Text>
                  </Flex>
                ) : (
                  <Box display={"grid"} gridTemplateColumns={"repeat(9, 1fr)"} gap="4px" fontSize={"12px"} textAlign={"center"}>
                    {publicHeaders.map((header, index) => (
                      <Box
                        zIndex={50}
                        transition={"box-shadow 0.3s ease"}
                        boxShadow={isScrolled ? "6px 6px 8px #00000020" : "none"}
                        position={"sticky"}
                        top={0}
                        key={index}
                        {...tableStyleHeader}
                        display="flex"
                        alignItems="center"
                      >
                        <Box w="100%">{header}</Box>
                      </Box>
                    ))}

                    {data?.columns?.slice(activePage * numberOfRowsPerPage, (activePage + 1) * numberOfRowsPerPage).map(({ values }, index, array) => {
                      const isLastRow = index === array.length - 1
                      const {
                        element_name, application_name, product_generic_code,
                        service_name, nominal_size, fitting_allowed, note, frl,
                        report_name, report_publish_status
                      } = values

                      return (
                        <React.Fragment key={`row-${index}`}>
                          <Flex {...columnStyles} {...nameColumnStyles} borderBottom={isLastRow ? "none" : `1px solid ${Colors.Secondary.c2_300}`}>
                            <Box w="100%">{element_name}</Box>
                          </Flex>
                          <Flex {...columnStyles} borderBottom={isLastRow ? "none" : `1px solid ${Colors.Secondary.c2_300}`}>
                            <Box w="100%">{application_name}</Box>
                          </Flex>
                          <Flex {...columnStyles} borderBottom={isLastRow ? "none" : `1px solid ${Colors.Secondary.c2_300}`}>
                            <Box w="100%">
                              <Button {...buttonProps} onClick={() => { handleProductClick(product_generic_code) }}>
                                {product_generic_code}
                              </Button>
                            </Box>
                          </Flex>
                          <Flex {...columnStyles} borderBottom={isLastRow ? "none" : `1px solid ${Colors.Secondary.c2_300}`}>
                            <Box w="100%">{service_name}</Box>
                          </Flex>
                          <Flex {...columnStyles} borderBottom={isLastRow ? "none" : `1px solid ${Colors.Secondary.c2_300}`}>
                            <Box w="100%">{nominal_size}</Box>
                          </Flex>
                          <Flex {...columnStyles} borderBottom={isLastRow ? "none" : `1px solid ${Colors.Secondary.c2_300}`}>
                            <Box w="100%">{fitting_allowed || "—"}</Box>
                          </Flex>
                          <Box {...columnStyles} borderBottom={isLastRow ? "none" : `1px solid ${Colors.Secondary.c2_300}`}>
                            {note && (() => {
                              try {
                                const notesData = typeof note === "string" ? JSON.parse(note) : note
                                return (
                                  <Flex gap="0.25" flexWrap="wrap" alignItems="center">
                                    {notesData.map((item: { note_id: number; notes: string }, i: number) => (
                                      <React.Fragment key={item.note_id}>
                                        <SnapPopoverNotes text={item.notes} triggerText={item.note_id.toString()} noteIcon={undefined} />
                                        {i < notesData.length - 1 && <Text as="span">,</Text>}
                                      </React.Fragment>
                                    ))}
                                  </Flex>
                                )
                              } catch {
                                return <SnapPopoverNotes text={note} triggerText="Note" noteIcon={undefined} />
                              }
                            })()}
                          </Box>
                          <Flex {...columnStyles} borderBottom={isLastRow ? "none" : `1px solid ${Colors.Secondary.c2_300}`}>
                            <Box w="100%">{frl}</Box>
                          </Flex>
                          {report_publish_status === "Public" ? (
                            <Flex {...columnStyles} borderBottom={isLastRow ? "none" : `1px solid ${Colors.Secondary.c2_300}`}>
                              <Box w="100%">
                                <Flex align="center" gap="4px">
                                  <Button
                                    variant="linkButton"
                                    p="0"
                                    minW="0"
                                    h="auto"
                                    display="flex"
                                    alignItems="center"
                                    flexShrink={0}
                                    onClick={() => { void handleDirectDownload(report_name) }}
                                    isLoading={downloadLoading === report_name}
                                    aria-label={`Download ${report_name}`}
                                  >
                                    <DownloadIcon fontSize="12px" />
                                  </Button>
                                  <Button
                                    variant="linkButton"
                                    fontWeight="normal"
                                    fontSize="12px"
                                    display="flex"
                                    alignItems="center"
                                    gap="4px"
                                    onClick={() => { void handleReportClick(report_name) }}
                                    isLoading={reportLoading}
                                    disabled={reportLoading}
                                  >
                                    <PDFIcon />
                                    {report_name}
                                  </Button>
                                </Flex>
                              </Box>
                            </Flex>
                          ) : (
                            <Flex {...columnStyles} borderBottom={isLastRow ? "none" : `1px solid ${Colors.Secondary.c2_300}`}>
                              <Box w="100%">
                                <Flex align="center" gap="4px">
                                  <Box p="0" minW="0" h="auto" visibility="hidden" aria-hidden="true">
                                    <DownloadIcon fontSize="12px" />
                                  </Box>
                                  <Tooltip label="* Please contact our customer support for the report." hasArrow placement="top">
                                    <Flex align="center" gap="4px" cursor="default">
                                      <PDFIcon />
                                      <Text as="span" fontWeight="normal">
                                        {report_name}
                                        {report_name ? <sup>*</sup> : ""}
                                      </Text>
                                    </Flex>
                                  </Tooltip>
                                </Flex>
                              </Box>
                            </Flex>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
        {Boolean(data?.columns?.length) && (
          <Text fontSize="12px" color={Colors.Secondary.c2} mt="2">
            * Please contact our customer support for the report.
          </Text>
        )}
      </Box>
    </>
  )
}

export default PublicFilterTable
